import {
  dataSourceToDataUrl,
  fetchSingleDataSource,
  getFormEntityByToken,
  resolveConditionalProperty,
  resolveDataFilters,
  selectContext,
  selectValues,
} from '@icp/form-renderer-core';
import { get, toPath } from 'lodash-es';
import { getTFunc } from '@icp/i18n';
import {
  escapeExcelFormulaInjection,
  escapeFilename,
  loadXlsx,
  resolveVariablePattern,
  toMap,
} from '@icp/utils';
import { getFilterType } from '@icp/components';
import { message } from '@icp/settings';
import {
  ACL_COLUMN,
  ACTION_COLUMN,
  AUTO_INDEX_COLUMN,
  GROUP_SELECT_COLUMN,
  NUMBER_COLUMN,
  SELECT_COLUMN,
  TEXT_COLUMN,
  TEXTAREA_COLUMN,
} from './constant';
import {
  AutoCellRenderer,
  cellRenderMapping,
  NumberCellRenderer,
  NumericCellRenderer,
} from './cellRenders';

// 因为用到了 AutoCellRenderer 不能放到 constants.js 里否则会报 import 循环错
export const DEFAULT_COL_DEF = {
  flex: 1,
  minWidth: 100,
  resizable: true,
  sortable: true,
  filter: 'agTextColumnFilter',
  enableRowGroup: false,
  cellRenderer: AutoCellRenderer,
  // export excel 会调用 valueFormatter
  valueFormatter: AutoCellRenderer.toDisplayText,
  filterParams: {
    maxNumConditions: 1,
  },
  // 禁止掉 ag-grid 的 cellDataType，否则当列有脏数据的时候（比如老数据是 string，后期把列改为调用 NumberCellEditor），编辑会无法修改数据。
  cellDataType: false,
  // filterValueGetter 给 Toolbar filterModel 精确搜索使用，在 clientSide 生效。需要同时兼容
  // 1. filterType 是 text 的普通输入框搜索，这种情况类似模糊搜索框，用所见即所得的字符串匹配；
  // 1. 用户配置了类型的精确 filter，例如 Number Date Set，这种需要保留数据的原始值，如 Number 和 Date 比较大小，
  // Set 使用 value 而不是用 label 匹配。
  filterValueGetter: (params) => {
    const { colDef, getValue } = params;
    const filterType = getFilterType(colDef);
    if (filterType === 'text') {
      // 如果用户没有配置改列的 Toolbar filter
      return DEFAULT_COL_DEF.getQuickFilterText(params);
    }
    return getValue(colDef.colId);
  },
  // getQuickFilterText 给 Toolbar 模糊搜索使用，在 clientSide 生效
  getQuickFilterText: (params) => {
    const { data, colDef, context } = params;

    // params.value 已经是 filterValueGetter 返回过后改变过后的值，不能直接使用。
    const value = get(data, colDef.field);
    if (value === undefined || value === null || !colDef.cellRenderer) {
      return value;
    }

    if (typeof colDef.cellRenderer.getQuickFilterText === 'function') {
      return colDef.cellRenderer.getQuickFilterText(params);
    }

    if (typeof colDef.cellRenderer.toDisplayText === 'function') {
      return colDef.cellRenderer.toDisplayText({ value, colDef, context });
    }
    return value;
  },
};

export function forEachColumnDefs(columnDefs, cb) {
  const dfs = (colDefArray) => {
    if (!Array.isArray(colDefArray)) {
      return;
    }

    for (const colDef of colDefArray) {
      cb(colDef);
      dfs(colDef.children);
    }
  };

  dfs(columnDefs);
}

export function flattenColumnDefs(columnDefs) {
  return columnDefs.flatMap((x) => (x.children ? flattenColumnDefs(x.children) : x));
}

async function getSetColumnFilterOptions({
  request,
  signal,
  colDef,
  tableDataSource,
  store,
  params,
  currentDataRef,
}) {
  const context = selectContext(store.getState());
  const formData = selectValues(store.getState());
  const { token: formEntityToken, pbcToken = context.pbcToken } = tableDataSource || {};

  const {
    stringEqual = true,
    mapping: mappingProp,
    dataSource,
    dataUrl: dataUrlProp,
    dataFilters,
    sortModel: sortModelProp,
    dataResponseKeyPath = 'results',
    transformDataResponse,
    translateDataResponse,
    defaultValue,
    httpMethod,
    httpBody,
    selectColId: selectColIdProp,
    // searchTextColIds,
  } = colDef.filterParams || {};

  let dataUrl;
  let mapping;
  let selectColId;

  if (dataSource || dataUrlProp) {
    const dataUrlConfig = dataUrlProp || dataSourceToDataUrl(dataSource, context);
    dataUrl = resolveVariablePattern({
      pattern: dataUrlConfig,
      currentData: currentDataRef.current || formData,
      formData,
      context,
      params,
      failWhenNoVariableValue: true,
    });
    mapping = mappingProp; // 要求如果 filterParams 配了 dataSource，必须指定 mapping
    selectColId = selectColIdProp;
  } else {
    await store
      .dispatch(getFormEntityByToken({ pbcToken, formEntityToken }))
      .unwrap()
      .then((entity) => {
        const field = entity.fields.find((f) => f.token === colDef.colId);
        const { referenceEntity, referencePbc, referenceField, referenceDisplayField } = field;
        dataUrl = `/form/api/v3/form-entity-data/${referencePbc}/${referenceEntity}/list`;
        mapping = { value: referenceField, label: referenceDisplayField };
        selectColId = [
          mapping.value,
          // mapping.label 可能是 abc[0].label 的形式，直接发给后端会报错，默认使用 selectColId 的目的只是为了
          // 减少数据加载，参数是字段 token，所以 abc[0].label 的配置 abc 才是 token
          toPath(referenceDisplayField)[0],
        ];
      });
  }

  const [filterModel] = resolveDataFilters({
    dataFilters,
    store,
    currentData: currentDataRef.current,
    params,
  });
  const sortModel = sortModelProp || [{ sort: 'asc', colId: mapping.label }];

  const { startRow, endRow, searchText } = request;

  return fetchSingleDataSource({
    signal,
    context,
    formData,
    searchText,
    // set filter 里目前也不支持自定义显示项，只会显示 label，所以只搜索可见的 label
    // 如果需要支持 api 搜索自定义 searchTextColIds，那需要同时修改 SetFilterValues.js 支持拿到所有数据后的前端搜索
    searchTextColIds: /* searchTextColIds?.length ? searchTextColIds :  */ [mapping.label],

    // lazy control
    startRow,
    endRow,

    // single data source config
    dataUrl,
    dataResponseKeyPath,
    filterModel,
    sortModel,
    transformDataResponse,
    translateDataResponse,
    defaultValue,
    httpMethod,
    httpBody,
    selectColId,
    needCount: false,
  }).then((res) => {
    return res.map((item) => {
      return {
        value: stringEqual ? String(item[mapping.value]) : item[mapping.value],
        label: get(item, mapping.label),
      };
    });
  });
}

function shouldUseCustomSetFilter(colDef, dataSource) {
  // 如果已经配置了静态 options，直接用静态 options，不需要从服务端拉取
  // 注意：此处判断逻辑需要和 FilterPanel/utils.js 中的 getFilterType、getSetFilterOptions 保持一致
  if (colDef.cellRendererParams?.options || colDef.cellEditorParams?.componentProps?.options) {
    return false;
  }

  // 自动只是表单数据字段里的 acl 和 select 获取后选项，
  // 使用表格数据源表单自动获取 acl 和 select 的数据源，或者自定义配置 dataSource 或者 dataUrl
  if (colDef.type === ACL_COLUMN || colDef.type === SELECT_COLUMN) {
    return (
      (!colDef.filter || colDef.filter === 'agSetColumnFilter') &&
      (dataSource?.token || colDef.filterParams?.dataSource || colDef.filterParams?.dataUrl)
    );
  }

  // 自定义配置，把任意列制定为 set 类型过滤并且设置 dataSource 或者 dataUrl
  if (colDef.filter === 'agSetColumnFilter') {
    return colDef.filterParams?.dataSource || colDef.filterParams?.dataUrl;
  }
  return false;
}

export function handleCustomSetFilters({ columnDefs, dataSource, store, params, currentDataRef }) {
  return columnDefs.map((item) => {
    const colDef = { ...item };
    // TODO, clientSide 下使用 getOptions 操作 filter 有一些怪还有点异常情况，现在没时间了回头看统一全部用后端
    // filter 还是做成和 ag-grid 一样的使用表格已有的数据作为 set filter 的 values
    if (shouldUseCustomSetFilter(colDef, dataSource)) {
      colDef.filter = 'agSetColumnFilter';
      colDef.filterParams = {
        // 这个函数只是为了防止 ag-grid 在 clientSide 下报错当有 keyCreator 时必须要求有 valueFormatter
        valueFormatter: (p) => p.value?.label,
        getOptions: (request, signal) =>
          getSetColumnFilterOptions({
            request,
            signal,
            colDef,
            tableDataSource: dataSource,
            store,
            params,
            currentDataRef,
          }),
        ...colDef.filterParams,
      };
    }
    return colDef;
  });
}

// 避免循环导入 columnTypes，hardcode 有 cellClass 的 type
const COLUMN_TYPE_CELL_CLASS = {
  [TEXT_COLUMN]: 'stringType',
  [TEXTAREA_COLUMN]: 'stringType',
};

// TODO, 处理有 column group children 的情况
export function formatColumnDefs(
  columnDefs,
  rowModelType,
  dataSource,
  store,
  routerParams,
  currentDataRef,
) {
  if (!Array.isArray(columnDefs)) {
    return [];
  }
  // “合并多行选择”列依赖按分组字段排序后的连续行；如果用户对其它列排序或分组，会打乱分组的相邻性，
  // 破坏 spanRows 合并与整组选择。所以一旦存在该类型的列，就禁用其它列的排序与分组
  // （该列自身保留 sortable 以应用初始 sort，让同组数据相邻）。
  const hasGroupSelect = columnDefs.some((item) => item.type === GROUP_SELECT_COLUMN);
  const formatted = columnDefs
    .filter((item) => !item.hidden)
    .map((item) => {
      const colDef = { ...item };
      // 针对特定 type 固定 colId 和 field。不能在 columnTypes.js 里写，ag-grid 的 columnTypes 里是缺省值的意思，会被 props 里 columnDefs 的属性覆盖掉。
      if (colDef.type === AUTO_INDEX_COLUMN) {
        colDef.colId = AUTO_INDEX_COLUMN;
        colDef.field = AUTO_INDEX_COLUMN; // field 在 export 的时候用
      }

      if (colDef.type === ACTION_COLUMN) {
        const t = getTFunc('icp-form-renderer');
        colDef.colId = item.colId ?? ACTION_COLUMN;
        colDef.field = null;
        colDef.headerName = colDef.headerName ?? t('action.header-name'); // 允许传 null 和空字符串不显示
      }

      if (hasGroupSelect && colDef.type !== GROUP_SELECT_COLUMN) {
        colDef.sortable = false;
        colDef.enableRowGroup = false;
        colDef.rowGroup = false;
      }

      // 根据 cellRendererParams.textAlign 设置 cellClass 控制对齐
      // 注意：colDef 上设了 cellClass 会覆盖 columnTypes 里的默认值，所以需要把 type 里已有的 cellClass 一并带上
      const { textAlign } = colDef.cellRendererParams || {};
      if (textAlign) {
        const alignClass = `table-cell-align-${textAlign}`;
        const existingClasses = [].concat(
          colDef.cellClass || COLUMN_TYPE_CELL_CLASS[colDef.type] || [],
        );
        colDef.cellClass = [...existingClasses, alignClass];
      }

      /* if (rowModelType !== 'clientSide') {
        // 目前后端 serverSide 模式不支持 group
        delete colDef.enableRowGroup;
      } */
      return colDef;
    });
  return handleCustomSetFilters({
    columnDefs: formatted,
    dataSource,
    store,
    params: routerParams,
    currentDataRef,
  });
}

export function resolveDefaultColumnDefs(
  defaultColDefProp = {},
  currentData = {},
  store = {},
  params = {},
) {
  const newDefaultColDef = { ...defaultColDefProp };
  Object.entries(newDefaultColDef).forEach(([key, value]) => {
    const [resolvedValue] = resolveConditionalProperty({
      value,
      currentData,
      store,
      params,
    });

    newDefaultColDef[key] = resolvedValue;
  });
  return newDefaultColDef;
}

export function getDisplayTextFunc(colDef, gridContext) {
  const cellRenderer =
    typeof colDef.cellRenderer === 'string'
      ? cellRenderMapping[colDef.cellRenderer]
      : colDef.cellRenderer;
  if (cellRenderer === NumberCellRenderer) {
    return (v) => NumberCellRenderer.parseValue(v, colDef.cellRendererParams);
  }
  if (cellRenderer === NumericCellRenderer) {
    // numeric 是高精度的，转成 number excel 会丢失精度，所以保留原始 string
    return (v) => v;
  }
  if (typeof cellRenderer?.toDisplayText === 'function') {
    return (v) => colDef.cellRenderer.toDisplayText({ value: v, colDef, context: gridContext });
  }
  if (!colDef.type) {
    return (v) => AutoCellRenderer.toDisplayText({ value: v });
  }
  return (v /* , o */) => {
    // if (o) {
    //   return get(o, colDef.field);
    // }
    return v;
  };
}

export function makeSettingKey(id, context, prefix, isGlobal) {
  const { pbcToken, formEntityToken, layoutToken, schemaId, userProfile } = context;

  if (!id) {
    return null;
  }

  let key = `${prefix}.${id}`;

  if (pbcToken) {
    key += `.${pbcToken}`;
  }

  if (schemaId) {
    key += `.${schemaId}`;
  } else if (formEntityToken && layoutToken) {
    key += `.${formEntityToken}.${layoutToken}`;
  }

  if (userProfile?.username && !isGlobal) {
    key += `.${userProfile.username}`;
  } else {
    key += '.all';
  }

  return key;
}

export function extractSortModel(columnState) {
  return columnState
    .filter((cs) => cs.sort)
    .sort((cs) => cs.sortIndex)
    .map((cs) => {
      return {
        colId: cs.colId,
        sort: cs.sort,
      };
    });
}

export function getExportableColumns(gridRef) {
  return gridRef.current.api
    .getAllGridColumns()
    .filter((column) => {
      const { colDef } = column;
      return (
        column.visible &&
        !(colDef.colId === undefined || colDef.colId === null) &&
        colDef.colId !== 'ag-Grid-ControlsColumn' &&
        colDef.colId !== 'ag-Grid-SelectionColumn' &&
        colDef.colId !== 'ag-Grid-RowNumbersColumn' &&
        colDef.type !== ACTION_COLUMN
      );
    })
    .map((column) => column.colDef);
}

export function getSelectedIds(gridApi) {
  const selectedRows = gridApi.getSelectedRows();
  return selectedRows.map((row) => row.id);
}

export function suppressEnterEventWhenEditing(params) {
  const isEnter = params.event.key === 'Enter';
  const showDelayStop = isEnter && params.editing;
  if (showDelayStop) {
    setTimeout(() => {
      params.api.stopEditing();
    }, 40);
  }
  return showDelayStop;
}

export function getDisplayedRowData(gridRef) {
  const rowData = [];
  gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
    rowData.push(node.data);
  });
  return rowData;
}

export function exportTableDataToExcel({
  gridRef,
  rowModelType,
  getRows,
  maxSizePerReq,
  excelExportFilename,
  formApi,
}) {
  const t = getTFunc('icp-form-renderer');

  const gridContext = gridRef.current.api.getGridOption('context');
  const sortModel = extractSortModel(gridRef.current.api.getColumnState());
  const filterModel = gridRef.current.getTableState().filterModel;
  const searchText = gridRef.current.getSearchText();

  let destroyLoadingMsg;

  const getAllData = () => {
    if (rowModelType === 'clientSide') {
      return Promise.resolve(getDisplayedRowData(gridRef));
    }

    destroyLoadingMsg = message.loading(t('message.exporting'), 0);

    // totalRowCount.current 存的是 table 有 filter 过后的总数，不能用，单独发个请求拿一下。
    return getRows({
      needCount: true,
      startRow: 0,
      endRow: 1,
      sortModel,
      filterModel,
      searchText,
    }).then(({ rowCount }) => {
      const reqCount = Math.ceil(rowCount / maxSizePerReq);
      const payloadList = new Array(reqCount).fill(0).map((_, i, arr) => {
        const startRow = i * maxSizePerReq;
        const endRow = i === arr.length - 1 ? rowCount : startRow + maxSizePerReq;
        return {
          startRow,
          endRow,
          sortModel,
          filterModel,
          searchText,
        };
      });
      return (
        Promise.all(payloadList.map((p) => getRows(p).then((res) => res.rowData)))
          // 拍平数据数组
          .then((dataList) => dataList.flatMap((x) => x))
      );
    });
  };

  const getFileName = () => {
    return `${escapeFilename(excelExportFilename) || formApi.getTitle() || 'export'}.xlsx`;
  };

  getAllData()
    // 转换格式 过滤字段
    .then((data) => {
      const colsToExport = getExportableColumns(gridRef);

      // 前端自动计算显示的 row index，data 里没有
      const hasAutoIndexCol = !!colsToExport.find((colDef) => colDef.type === AUTO_INDEX_COLUMN);
      if (hasAutoIndexCol) {
        data.forEach((item, index) => {
          item[AUTO_INDEX_COLUMN] = index + 1;
        });
      }

      const valueTransformerMap = toMap(
        (colDef) => colDef.colId,
        (colDef) => getDisplayTextFunc(colDef, gridContext),
      )(colsToExport);

      // rows 数据转换为 { [headerName]: v } 的形式，以 headerName 为 key
      const rows = data.map((item) => {
        return Object.fromEntries(
          colsToExport.map((colDef) => {
            const v = get(item, colDef.field);
            const originalCellValue = valueTransformerMap[colDef.colId](v, item);
            const escapedCellValue = escapeExcelFormulaInjection(originalCellValue);
            return [colDef.headerName, escapedCellValue];
          }),
        );
      });

      // check duplicated headerName
      const headerNames = new Set();
      colsToExport.forEach((colDef) => {
        if (!headerNames.has(colDef.headerName)) {
          headerNames.add(colDef.headerName); // TODO 没用headerName而是用headerValueGetter表达式的
        } else {
          message.warning(`有多列名字叫 ${colDef.headerName}，会导致导出数据不对`);
        }
      });
      const colHeaders = Array.from(headerNames);

      // 由于 xlsx 的 worksheet 的格式需求，所以 formats 必须是和 colHeaders 数组一一对应的 index
      const formats = [];
      colHeaders.forEach((headerName, index) => {
        // headerName 可能会有重复，colHeaders 里去重了，所以这里使用找到的第一个匹配项
        const colDef = colsToExport.find((cd) => cd.headerName === headerName);
        if (!formats[colDef.headerName]) {
          // 暂时只处理 number 的 format，后面应该完善整个 date 等的格式化
          if (colDef.type === NUMBER_COLUMN) {
            // 根据规则，目前只针对设置了 minimumFractionDigits, maximumFractionDigits number 列进行导出格式化，
            // 有需求可以修改
            const { minimumFractionDigits, maximumFractionDigits } =
              colDef.cellRendererParams?.formatOptions || {};
            if (
              typeof minimumFractionDigits === 'number' &&
              typeof maximumFractionDigits === 'number'
            ) {
              formats[index] = {
                formatOptions: colDef.cellRendererParams?.formatOptions,
              };
            }
          }
        }
      });

      return [rows, colHeaders, formats];
    })
    // 导出
    .then(([data, colHeaders, formats]) => {
      return loadXlsx().then((XLSX) => {
        const worksheet = XLSX.utils.json_to_sheet(data, { header: colHeaders });
        const range = XLSX.utils.decode_range(worksheet['!ref']);

        // format cell
        // 官方没有统一给一列设置 format 的方法，只能每个 cell 单独格式化，
        // 以下代码参考自 https://github.com/SheetJS/sheetjs/issues/885
        // Official Doc: https://docs.sheetjs.com/docs/csf/features/nf/
        for (let i = range.s.r + 1; i <= range.e.r; i++) {
          for (let j = range.s.c + 1; j <= range.e.c; j++) {
            // find the data cell (range.s.r + 1 skips the header row of the worksheet)
            const cellRef = XLSX.utils.encode_cell({ r: i, c: j });
            // if the particular row did not contain data for the column, the cell will not be generated
            if (!worksheet[cellRef]) continue;
            // `.t == "n"` for number cells
            if (worksheet[cellRef].t !== 'n') continue;
            if (!formats[j]) continue;
            // `.z` property means number format
            const { minimumFractionDigits } = formats[j].formatOptions;
            worksheet[cellRef].z = `#,##0.${''.padStart(minimumFractionDigits, '0')}`;
          }
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet);
        XLSX.writeFile(workbook, getFileName(), {
          bookType: 'xlsx',
          compression: true,
        });
      });
    })
    .catch((e) => {
      console.error(e);
      message.error(t('error.export'));
    })
    .finally(() => {
      if (destroyLoadingMsg) {
        destroyLoadingMsg();
      }
    });
}
